import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const paymentTypeLabel = (type) => {
  if (type === "deposit") return "depósito del 30%";
  if (type === "final") return "pago final";
  return "pago";
};

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const requestIdFromUrl = params.get("request_id");
  const [state, setState] = useState("checking"); // checking|paid|expired|error
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async (n = 0) => {
      if (cancelled || n >= 12) {
        if (!cancelled) setState((s) => s === "paid" ? s : "error");
        return;
      }
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.status === "expired") { setState("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const requestId = info?.request_id || requestIdFromUrl;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="max-w-2xl mx-auto px-5 md:px-8 py-20">
        <div className="sc-card rounded-md p-10 text-center" data-testid="payment-return-card">
          {state === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-[#FFC107] animate-spin mx-auto mb-5" />
              <h1 className="font-display font-bold text-3xl mb-2">Verificando pago…</h1>
              <p className="text-[#A1A1AA]">Confirmando con Stripe (intento {attempts + 1}/12)</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
              </div>
              <h1 className="font-display font-bold text-4xl mb-3">¡Pago confirmado!</h1>
              <p className="text-[#A1A1AA] mb-6">Tu {paymentTypeLabel(info?.payment_type)} ha sido recibido. Ya estamos en camino.</p>
              {requestId && (
                <Link to={`/seguimiento/${requestId}`} className="btn-amber px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2 text-sm" data-testid="go-tracking">
                  Ver seguimiento <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </>
          )}
          {state === "expired" && (
            <>
              <XCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-5" />
              <h1 className="font-display font-bold text-3xl mb-2">Sesión expirada</h1>
              <p className="text-[#A1A1AA] mb-6">La sesión de pago ha caducado. Puedes reintentarlo.</p>
              <button onClick={() => navigate("/reservar")} className="btn-amber px-6 py-3 rounded-md font-semibold">Volver a reservar</button>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-5" />
              <h1 className="font-display font-bold text-3xl mb-2">No pudimos verificar</h1>
              <p className="text-[#A1A1AA] mb-6">Revisa tu email o contacta con nosotros.</p>
              <Link to="/" className="btn-outline px-6 py-3 rounded-md font-semibold inline-block">Volver al inicio</Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
